/**
 * RSI Analyzer Types
 *
 * Type definitions for the RSI (Relative Strength Index) Analyzer.
 * These types define the API contract between TypeScript and Rust.
 * The Rust implementation mirrors these types with serde annotations.
 *
 * @see docs/RSI_ANALYZER_DESIGN.md for detailed design documentation
 */
// ============================================================================
// Enums
// ============================================================================
/**
 * RSI Zone Classification
 * 7 zones providing granular classification of RSI values
 */
export var RSIZone;
(function (RSIZone) {
    /** RSI 0-20: Panic selling, potential capitulation */
    RSIZone["extremeOversold"] = "extremeOversold";
    /** RSI 20-30: Standard oversold, traditional buy zone */
    RSIZone["oversold"] = "oversold";
    /** RSI 30-40: Approaching opportunity */
    RSIZone["nearOversold"] = "nearOversold";
    /** RSI 40-60: No strong signal */
    RSIZone["neutral"] = "neutral";
    /** RSI 60-70: Approaching caution */
    RSIZone["nearOverbought"] = "nearOverbought";
    /** RSI 70-80: Standard overbought, traditional sell zone */
    RSIZone["overbought"] = "overbought";
    /** RSI 80-100: Euphoria, potential blow-off top */
    RSIZone["extremeOverbought"] = "extremeOverbought";
})(RSIZone || (RSIZone = {}));
/**
 * RSI Trend Classification
 * Based on rate of change (points per bar)
 */
export var RSITrend;
(function (RSITrend) {
    /** rateOfChange > 5 points/bar */
    RSITrend["risingFast"] = "risingFast";
    /** 1 < rateOfChange <= 5 points/bar */
    RSITrend["rising"] = "rising";
    /** -1 <= rateOfChange <= 1 points/bar */
    RSITrend["flat"] = "flat";
    /** -5 <= rateOfChange < -1 points/bar */
    RSITrend["falling"] = "falling";
    /** rateOfChange < -5 points/bar */
    RSITrend["fallingFast"] = "fallingFast";
})(RSITrend || (RSITrend = {}));
/**
 * RSI Divergence Types
 */
export var RSIDivergenceType;
(function (RSIDivergenceType) {
    /** Price lower low, RSI higher low (reversal up) */
    RSIDivergenceType["bullish"] = "bullish";
    /** Price higher high, RSI lower high (reversal down) */
    RSIDivergenceType["bearish"] = "bearish";
    /** Price higher low, RSI lower low (continuation up) */
    RSIDivergenceType["hiddenBullish"] = "hiddenBullish";
    /** Price lower high, RSI higher high (continuation down) */
    RSIDivergenceType["hiddenBearish"] = "hiddenBearish";
})(RSIDivergenceType || (RSIDivergenceType = {}));
/**
 * RSI Divergence Strength Classification
 */
export var RSIDivergenceStrength;
(function (RSIDivergenceStrength) {
    /** RSI divergence > 10 points AND price divergence > 1% */
    RSIDivergenceStrength["strong"] = "strong";
    /** RSI divergence 5-10 points */
    RSIDivergenceStrength["moderate"] = "moderate";
    /** RSI divergence < 5 points */
    RSIDivergenceStrength["weak"] = "weak";
})(RSIDivergenceStrength || (RSIDivergenceStrength = {}));
/**
 * RSI Event Types
 * All possible events generated by the RSI analyzer
 */
export var RSIEventType;
(function (RSIEventType) {
    // === Zone Transitions ===
    RSIEventType["enteredExtremeOversold"] = "enteredExtremeOversold";
    RSIEventType["enteredOversold"] = "enteredOversold";
    RSIEventType["exitedOversold"] = "exitedOversold";
    RSIEventType["enteredOverbought"] = "enteredOverbought";
    RSIEventType["exitedOverbought"] = "exitedOverbought";
    RSIEventType["enteredExtremeOverbought"] = "enteredExtremeOverbought";
    // === Centerline ===
    RSIEventType["crossedAbove50"] = "crossedAbove50";
    RSIEventType["crossedBelow50"] = "crossedBelow50";
    // === MA Crossovers ===
    RSIEventType["crossedAboveMA"] = "crossedAboveMA";
    RSIEventType["crossedBelowMA"] = "crossedBelowMA";
    // === Divergences (longer durations only) ===
    RSIEventType["bullishDivergenceDetected"] = "bullishDivergenceDetected";
    RSIEventType["bearishDivergenceDetected"] = "bearishDivergenceDetected";
    RSIEventType["hiddenBullishDivergence"] = "hiddenBullishDivergence";
    RSIEventType["hiddenBearishDivergence"] = "hiddenBearishDivergence";
    RSIEventType["divergenceConfirmed"] = "divergenceConfirmed";
    RSIEventType["divergenceExpired"] = "divergenceExpired";
    // === Local Extremes (always available) ===
    RSIEventType["newLocalLow"] = "newLocalLow";
    RSIEventType["newLocalHigh"] = "newLocalHigh";
    // === Bounce Points (longer durations only) ===
    RSIEventType["enteredBullishBounceZone"] = "enteredBullishBounceZone";
    RSIEventType["enteredBearishBounceZone"] = "enteredBearishBounceZone";
    // === Confluence (longer durations only) ===
    RSIEventType["allPeriodsOversold"] = "allPeriodsOversold";
    RSIEventType["allPeriodsOverbought"] = "allPeriodsOverbought";
    RSIEventType["periodsConverging"] = "periodsConverging";
    RSIEventType["periodsDiverging"] = "periodsDiverging";
    // === Embedded Extremes ===
    RSIEventType["becameEmbeddedOversold"] = "becameEmbeddedOversold";
    RSIEventType["becameEmbeddedOverbought"] = "becameEmbeddedOverbought";
    // === Recovery/Failure ===
    RSIEventType["oversoldRecoveryStarted"] = "oversoldRecoveryStarted";
    RSIEventType["overboughtRolloverStarted"] = "overboughtRolloverStarted";
    RSIEventType["failedOversoldBreakout"] = "failedOversoldBreakout";
    RSIEventType["failedOverboughtBreakout"] = "failedOverboughtBreakout";
})(RSIEventType || (RSIEventType = {}));
//# sourceMappingURL=RSIAnalyzerTypes.js.map